import { ReactNode, useMemo, useState } from 'react';

import { CheckIcon, FunnelIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

export interface SimpleFilterComboboxOption {
  label: string;
  value: string;
}

export interface SimpleFilterComboboxProps {
  options: SimpleFilterComboboxOption[];
  value: string;
  onChange: (value: string) => void;
  icon?: ReactNode;
}

export const SimpleFilterCombobox = ({
  options,
  value,
  onChange,
  icon = <FunnelIcon />,
}: SimpleFilterComboboxProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selectedLabel = useMemo(
    () => options.find((o) => o.value === value)?.label || '',
    [options, value]
  );

  const filteredOptions = useMemo(() => {
    if (!search.trim()) {
      return options;
    }
    return options.filter((opt) => opt.label.toLowerCase().includes(search.toLowerCase()));
  }, [options, search]);

  const handleSelect = (selectedValue: string) => {
    onChange(selectedValue);
    setOpen(false);
    setSearch('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline-neutral"
          size="sm"
          role="combobox"
          aria-expanded={open}
          aria-controls="simple-filter-combobox-listbox"
        >
          {icon}
          {selectedLabel}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="min-w-40 p-0" align="end">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={t('common.search_dots')}
            value={search}
            onValueChange={setSearch}
          />
          <CommandList id="simple-filter-combobox-listbox" className="p-1">
            <CommandEmpty>{t('common.no_results')}</CommandEmpty>
            {filteredOptions.map((opt) => (
              <CommandItem key={opt.value} value={opt.value} onSelect={handleSelect}>
                <span>{opt.label}</span>
                {value === opt.value && <CheckIcon className="ml-auto text-accent" />}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
